import type { ApiPlayable } from "@hive/sdk";
import { catalogueComposition, type MosaicTile } from "./catalogueLayout";
import { playableLevel } from "./catalogueLevel";

interface FeedBand {
	tiles: MosaicTile<ApiPlayable>[];
	columns: number;
	rows: number;
	variant: number;
}
interface FeedLayoutOptions { columns: number; seed: string; compact: boolean; pattern?: number }
export interface CatalogueFeedLayout {
	options: FeedLayoutOptions;
	sourceKeys: string[];
	bands: FeedBand[];
	tail: FeedBand | null;
}

/** Neighbouring CMS entries get different openings; each page walks all twelve patterns. */
function bandPattern(seed: string, band: number, opening?: number) {
	const hash = Array.from(seed).reduce((value, char) => (Math.imul(value, 31) + char.charCodeAt(0)) >>> 0, 0);
	const step = [1, 5, 7, 11][Math.floor(hash / 12) % 4]!;
	return ((opening ?? hash) + band * step) % 12;
}

/** Append complete bands; only the unfinished bottom row can change on pagination. */
export function catalogueFeedLayout(items: ApiPlayable[], options: FeedLayoutOptions, previous?: CatalogueFeedLayout): CatalogueFeedLayout {
	const sourceKeys = items.map((item) => JSON.stringify([item.id, playableLevel(item), !!item.coverUrl]));
	const append = previous && previous.options.columns === options.columns && previous.options.seed === options.seed &&
		previous.options.pattern === options.pattern &&
		previous.options.compact === options.compact && previous.sourceKeys.length <= sourceKeys.length &&
		previous.sourceKeys.every((key, index) => key === sourceKeys[index]);
	const bands = append ? previous.bands.map((band) => ({ ...band,
		tiles: band.tiles.map((tile) => ({ ...tile, item: items[tile.sourceIndex]! })),
	})) : [];
	const placed = new Set(bands.flatMap((band) => band.tiles.map((tile) => tile.sourceIndex)));
	let remaining = items.map((item, sourceIndex) => ({ item, sourceIndex })).filter((entry) => !placed.has(entry.sourceIndex));
	let tail: FeedBand | null = null;
	while (remaining.length) {
		const pattern = bandPattern(options.seed, bands.length, options.pattern);
		const layout = catalogueComposition(remaining, (entry) => entry.item, (entry) => entry.item.coverUrl,
			{ columns: options.columns, pattern, compact: options.compact });
		const band = { columns: layout.columns, rows: layout.rows, variant: layout.variant,
			tiles: layout.tiles.map((tile) => ({ ...tile, item: tile.item.item, sourceIndex: tile.item.sourceIndex })),
		};
		if (band.tiles.reduce((area, tile) => area + tile.span ** 2, 0) < band.columns * band.rows) {
			tail = band;
			break;
		}
		bands.push(band);
		remaining = layout.rest.map((entry) => entry.item);
	}
	return { options, sourceKeys, bands, tail };
}
